<template>
  <!-- ປະຫວັດການຫັກໜີ້ — how advance debt moved, date by date: the hourly
       automatic deductions, deductions at withdrawal, write-offs (and, on
       request, transfers and reversals), each with the balance it left. Every
       figure is stored by the ledger as it happened; nothing is re-derived. -->
  <div class="debt-history">
    <div class="list-head">
      <h2 class="list-title">
        <i class="fal fa-history"></i>
        ປະຫວັດການຫັກໜີ້ຖອນ COD ລ່ວງໜ້າ
        <span class="list-count">({{ pagination.total }})</span>
      </h2>
      <p class="list-total">{{ filters.allDates ? 'ຍອດຫັກໜີ້ທັງໝົດ' : 'ຍອດຫັກໜີ້ໃນຊ່ວງນີ້' }} : {{ formatKip(totals.deducted || 0) }}</p>
      <button type="button" class="cod-btn is-pale list-head-action" :disabled="exporting" @click="exportRows">
        <i class="fal fa-file-excel"></i> {{ exporting ? 'ກຳລັງສົ່ງອອກ...' : 'Export' }}
      </button>
    </div>

    <div class="filter-card">
      <div class="filter-field">
        <label for="cod-debt-from">ຈາກວັນທີ:</label>
        <div class="day-step">
          <button class="round-btn is-step" type="button" aria-label="ມື້ກ່ອນ" title="ມື້ກ່ອນ"
                  :disabled="filters.allDates" @click="shiftDays(-1)">
            <i class="fal fa-chevron-left"></i>
          </button>
          <input id="cod-debt-from" class="filter-input is-date" type="date" v-model="filters.from"
                 :disabled="filters.allDates" @change="onDates">
        </div>
      </div>
      <div class="filter-field">
        <label for="cod-debt-to">ຫາວັນທີ:</label>
        <div class="day-step">
          <input id="cod-debt-to" class="filter-input is-date" type="date" v-model="filters.to"
                 :disabled="filters.allDates" @change="onDates">
          <button class="round-btn is-step" type="button" aria-label="ມື້ຖັດໄປ" title="ມື້ຖັດໄປ"
                  :disabled="filters.allDates" @click="shiftDays(1)">
            <i class="fal fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <!-- ບໍ່ກຳນົດວັນທີ: search the whole history, e.g. by a shop or an invoice number. -->
      <label class="all-dates" for="cod-debt-all-dates">
        <input id="cod-debt-all-dates" type="checkbox" v-model="filters.allDates" @change="fetchData(1)">
        <span>ທຸກວັນທີ</span>
      </label>
      <div class="filter-field">
        <label for="cod-debt-kind">ປະເພດ:</label>
        <select id="cod-debt-kind" class="filter-input is-wide" v-model="filters.kind" @change="fetchData(1)">
          <option v-for="k in kinds" :key="k.value" :value="k.value">{{ k.label }}</option>
        </select>
      </div>
      <div class="filter-field">
        <label for="cod-debt-search">ຄົ້ນຫາ:</label>
        <input id="cod-debt-search" class="filter-input is-wide" type="text" v-model="filters.search"
               placeholder="ຊື່ຮ້ານ / ເບີໂທ / ເລກອິນວອຍ" @keyup.enter="fetchData(1)">
      </div>
      <button class="round-btn" type="button" aria-label="ຄົ້ນຫາ" @click="fetchData(1)">
        <i class="fal fa-search"></i>
      </button>
    </div>

    <!-- A search by an advance's invoice number: that advance's own figures. The
         rows below may also repay other advances, so their amounts summed would
         overstate what went to this one. -->
    <div class="advance-card" v-for="a in advances" :key="'adv-' + a.id">
      <button type="button" class="link-btn advance-title" @click="openInvoice(a.invoice_id)">
        <i class="fal fa-file-invoice"></i> ລາຍການຖອນລ່ວງໜ້າ {{ a.invoice_number }}
      </button>
      <span>ໜີ້ທັງໝົດ <b>{{ formatKip(a.advance_amount) }}</b></span>
      <span v-if="a.recovered">ຫັກຈາກ COD <b class="text-ok">{{ formatKip(a.recovered) }}</b></span>
      <span v-if="a.repaid">ຮັບຊຳລະ <b class="text-ok">{{ formatKip(a.repaid) }}</b></span>
      <span v-if="a.written_off">ຕັດໜີ້ <b>{{ formatKip(a.written_off) }}</b></span>
      <span>ຍອດຄ້າງ <b :class="{'text-bad': a.outstanding > 0}">{{ formatKip(a.outstanding) }}</b></span>
    </div>

    <!-- One chip per day in the range; picking one narrows the table to it. -->
    <div class="day-strip" v-if="days.length">
      <button v-for="d in days" :key="d.date" type="button" class="day-chip"
              :class="{'is-active': filters.from === d.date && filters.to === d.date}"
              @click="pickDay(d.date)">
        <b class="day-date">{{ formatDateTime(d.date) }}</b>
        <template v-for="line in dayLines(d)">
          <span :key="line.kind" class="day-line" :class="{'is-up': line.up}">{{ line.label }} {{ formatKip(line.amount) }}</span>
          <!-- The invoices behind the figure, so it can be traced (newest few; the rest counted). -->
          <span v-if="line.invoices" :key="line.kind + '-inv'" class="day-invoices">
            <span v-for="number in line.invoices.numbers" :key="number">{{ number }}</span>
            <span v-if="line.invoices.more" class="day-more">+{{ line.invoices.more }} ອື່ນໆ</span>
          </span>
        </template>
        <span class="day-sum">ຫັກລວມ {{ formatKip(d.deducted) }}</span>
      </button>
    </div>

    <div class="table-card">
      <vs-table :data="rows">
        <template #thead>
          <vs-tr>
            <vs-th>ວັນທີ</vs-th>
            <vs-th>ຊື່ຮ້ານ</vs-th>
            <vs-th>ເບີໂທ</vs-th>
            <vs-th>ປະເພດ</vs-th>
            <vs-th>ອິນວອຍ</vs-th>
            <vs-th>ຈຳນວນ</vs-th>
            <vs-th>ຍອດຄ້າງຫຼັງລາຍການ</vs-th>
            <vs-th>ໝາຍເຫດ</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr :key="row.id" v-for="row in rows">
            <vs-td>{{ formatDateTime(row.at) }}</vs-td>
            <vs-td>{{ row.shop.name || row.customer.name || '—' }}</vs-td>
            <vs-td>{{ row.customer.tel || '—' }}</vs-td>
            <vs-td>
              <span class="kind-pill" :class="'is-' + row.kind">{{ row.kind_label }}</span>
            </vs-td>
            <vs-td>
              <!-- A repayment has no invoice: its evidence is the slip. -->
              <button v-if="row.slip_url" type="button" class="link-btn" @click="openSlip(row)">
                <i class="fal fa-receipt"></i> ເບິ່ງສະລິບ
              </button>
              <!-- The invoice, openable, with its own figures: what its bills
                   hold, an advance, and what it pays the shop next to whether
                   that was paid — a pending payout is not money sent. -->
              <template v-else-if="row.invoice">
                <button type="button" class="link-btn" :title="'ເບິ່ງອິນວອຍ ' + row.invoice.number"
                        @click="openInvoice(row.invoice.id)">
                  {{ row.invoice.number }} <i class="fal fa-eye"></i>
                </button>
                <span v-for="line in invoiceLines(row.invoice)" :key="line" class="cell-note is-figure">{{ line }}</span>
              </template>
              <span v-else>{{ row.invoice_number || '—' }}</span>
              <!-- ຫັກໃຫ້: the advance(s) this deduction paid off (owner, 2026-10-08),
                   with each one's share when it paid more than one, and the
                   advance's whole amount — the debt the deduction came off. -->
              <template v-for="(a, i) in (row.repaid_advances || [])">
                <span class="cell-note" :key="'ra-' + i">
                  ຫັກໃຫ້
                  <button v-if="a.invoice_id" type="button" class="link-btn is-note" :title="'ເບິ່ງອິນວອຍ ' + a.invoice_number"
                          @click="openInvoice(a.invoice_id)">{{ a.invoice_number || '—' }}</button>
                  <template v-else>{{ a.invoice_number || '—' }}</template>
                  <template v-if="row.repaid_advances.length > 1"> · {{ formatKip(a.amount) }}</template>
                </span>
                <span v-if="a.advance_amount" class="cell-note" :key="'rt-' + i">ໜີ້ທັງໝົດ {{ formatKip(a.advance_amount) }}</span>
              </template>
            </vs-td>
            <!-- The debt's change: − brought it down, + raised it. -->
            <vs-td>
              <span :class="change(row).tone">{{ change(row).text }}</span>
            </vs-td>
            <vs-td>
              <span :class="{'text-bad': row.balance_after > 0}">{{ formatKip(row.balance_after) }}</span>
            </vs-td>
            <vs-td>
              <span v-if="row.reason">{{ row.reason }}</span>
              <span class="cell-note" v-if="row.paid_at">ຮ້ານຊຳລະວັນທີ {{ formatDateTime(row.paid_at) }}</span>
              <span class="cell-note" v-if="row.by">ໂດຍ {{ row.by }}</span>
              <span v-if="!row.reason && !row.by && !row.paid_at" class="text-muted">—</span>
            </vs-td>
          </vs-tr>
        </template>
        <template #footer>
          <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="10" @paginate="fetchData"/>
        </template>
      </vs-table>

      <p class="list-state is-loading" v-if="isLoading">ກຳລັງໂຫລດ...</p>
      <p class="list-state" v-else-if="!rows.length">{{ filters.allDates ? 'ບໍ່ພົບລາຍການ' : 'ບໍ່ມີລາຍການໃນຊ່ວງວັນທີນີ້' }}</p>
      <p class="list-state is-error" v-if="error">{{ error }}</p>
    </div>

    <vs-dialog v-model="slipActive" v-if="slipActive" width="560px">
      <template #header>
        <h4 class="dialog-title">ສະລິບການຊຳລະ</h4>
      </template>
      <div class="slip-view">
        <div class="info-box">
          <div class="info-row">
            <span>ຮ້ານຄ້າ</span>
            <b>{{ slip.shopName }}</b>
          </div>
          <div class="info-row">
            <span>ຈຳນວນເງິນ</span>
            <b>{{ formatKip(slip.amount) }}</b>
          </div>
          <div class="info-row" v-if="slip.paidAt">
            <span>ວັນທີຊຳລະ</span>
            <b>{{ formatDateTime(slip.paidAt) }}</b>
          </div>
        </div>
        <img v-if="!slip.failed" class="slip-image" :src="slip.url" alt="ສະລິບການຊຳລະ" @error="slip.failed = true">
        <p v-else class="list-state is-error">ໂຫລດຮູບສະລິບບໍ່ໄດ້</p>
      </div>
    </vs-dialog>

    <!-- What an invoice holds: its figures, each under its own name, and its bills. -->
    <vs-dialog v-model="invoiceActive" v-if="invoiceActive" width="760px">
      <template #header>
        <h4 class="dialog-title">ອິນວອຍ {{ inv ? inv.number : '' }}</h4>
      </template>
      <p class="list-state is-loading" v-if="invoiceLoading && !inv">ກຳລັງໂຫລດ...</p>
      <p class="list-state is-error" v-if="invoiceError">{{ invoiceError }}</p>
      <div class="invoice-view" v-if="inv">
        <div class="info-box">
          <div class="info-row">
            <span>ປະເພດ</span>
            <b>{{ inv.type_label }}</b>
          </div>
          <div class="info-row">
            <span>ຮ້ານຄ້າ</span>
            <b>{{ inv.shop.name || inv.customer.name || '—' }}<template v-if="inv.customer.tel"> · {{ inv.customer.tel }}</template></b>
          </div>
          <div class="info-row">
            <span>ວັນທີ</span>
            <b>{{ formatDateTime(inv.created_at) }}</b>
          </div>
          <div class="info-row" v-if="inv.qty">
            <span>ຍອດ COD ໃນອິນວອຍ ({{ inv.qty }} ບິນ)</span>
            <b>{{ formatKip(inv.cod_total) }}</b>
          </div>
          <template v-if="inv.advance_amount">
            <div class="info-row">
              <span>ເງິນລ່ວງໜ້າ</span>
              <b>{{ formatKip(inv.advance_amount) }}</b>
            </div>
            <div class="info-row">
              <span>ຄ່າທຳນຽມ</span>
              <b>{{ formatKip(inv.advance_fee) }}</b>
            </div>
          </template>
          <div class="info-row" v-if="inv.deducted">
            <span>ຫັກໜີ້ COD ລ່ວງໜ້າ</span>
            <b class="text-ok">−{{ formatKip(inv.deducted) }}</b>
          </div>
          <div class="info-row" v-if="inv.payout">
            <span>ຍອດໂອນໃຫ້ຮ້ານ</span>
            <b class="is-amount">{{ formatKip(inv.payout) }}</b>
          </div>
          <div class="info-row">
            <span>ສະຖານະ</span>
            <b :class="{'text-bad': inv.status === 'rejected', 'text-busy': inv.status === 'pending' || inv.status === 'claimed'}">
              {{ inv.status_label }}<template v-if="inv.paid_at && inv.payout"> · {{ formatDateTime(inv.paid_at) }}</template>
            </b>
          </div>
          <p class="info-note" v-if="inv.legacy_page">ເງິນເຫຼືອນີ້ ໂອນຜ່ານໜ້າ COD ຈັດສົ່ງສຳເລັດ (ໜ້າເກົ່າ), ບໍ່ແມ່ນໜ້າຖອນລ່ວງໜ້າ.</p>
          <p class="info-note" v-if="inv.remark">{{ inv.remark }}</p>
          <p class="info-note text-bad" v-if="inv.deleted">ອິນວອຍນີ້ຖືກລຶບແລ້ວ</p>
        </div>

        <!-- An advance's invoice holds no bills: the advance's own figures instead. -->
        <div class="info-box" v-if="invoiceView.advance">
          <div class="info-row">
            <span>ໜີ້ທັງໝົດ</span>
            <b>{{ formatKip(invoiceView.advance.advance_amount) }}</b>
          </div>
          <div class="info-row" v-if="invoiceView.advance.recovered">
            <span>ຫັກຈາກ COD ແລ້ວ</span>
            <b class="text-ok">{{ formatKip(invoiceView.advance.recovered) }}</b>
          </div>
          <div class="info-row" v-if="invoiceView.advance.repaid">
            <span>ຮັບຊຳລະແລ້ວ</span>
            <b class="text-ok">{{ formatKip(invoiceView.advance.repaid) }}</b>
          </div>
          <div class="info-row" v-if="invoiceView.advance.written_off">
            <span>ຕັດໜີ້</span>
            <b>{{ formatKip(invoiceView.advance.written_off) }}</b>
          </div>
          <div class="info-row">
            <span>ຍອດຄ້າງ</span>
            <b :class="invoiceView.advance.outstanding > 0 ? 'text-bad' : 'text-ok'">{{ formatKip(invoiceView.advance.outstanding) }}</b>
          </div>
          <button type="button" class="cod-btn is-pale trace-btn" @click="traceInvoice(inv.number)">
            <i class="fal fa-history"></i> ເບິ່ງການຫັກໜີ້ຂອງລາຍການນີ້
          </button>
        </div>

        <template v-if="invoiceView.total">
          <table class="bill-table">
            <thead>
              <tr>
                <th>#</th>
                <th>ເລກບິນ</th>
                <th class="is-num">ຍອດ COD</th>
                <th>ວັນທີຮັບເງິນ COD</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, i) in invoiceView.bills" :key="b.shipment_number + '-' + i">
                <td>{{ (invoiceView.current_page - 1) * invoiceView.per_page + i + 1 }}</td>
                <td>{{ b.shipment_number || '—' }}</td>
                <td class="is-num">{{ formatKip(b.amount) }}</td>
                <td>{{ formatDateTime(b.paid_at) }}</td>
              </tr>
            </tbody>
          </table>
          <Pagination v-if="invoiceView.last_page > 1" :pagination="invoicePagination" :offset="6" @paginate="loadInvoicePage"/>
        </template>
        <!-- A rejection hard-deletes the bills (they can be requested again); the figures stay. -->
        <p class="list-state" v-else-if="inv.status === 'rejected'">ອິນວອຍຖືກປະຕິເສດ — ບິນຖືກປົດອອກແລ້ວ</p>
        <p class="list-state" v-else-if="!invoiceView.advance">ບໍ່ມີບິນໃນອິນວອຍນີ້</p>
      </div>
    </vs-dialog>
  </div>
</template>

<script>
import Pagination from '@/Plugins/Paginations/Pagination';
import {
  blobErrorMessage,
  downloadDebtHistoryExport,
  errorMessage,
  fetchDebtHistory,
  fetchDebtHistoryInvoice,
  formatDateTime,
  formatKip,
} from '@/services/codAdvanceService';

// The server refuses a wider range (a report, not a screen).
const MAX_DAYS = 92;

// A day card's lines, in order; `up` = the debt went up.
const DAY_KINDS = [
  {kind: 'auto_recovery', label: 'ອັດຕະໂນມັດ'},
  {kind: 'repayment', label: 'ຮັບຊຳລະ'},
  // History only (none since 2026-10-01): kept so ຫັກລວມ still adds up on old days.
  {kind: 'withdraw_recovery', label: 'ຕອນຖອນເງິນ'},
  {kind: 'write_off', label: 'ຕັດໜີ້'},
  {kind: 'disbursed', label: 'ໂອນລ່ວງໜ້າ', up: true},
  {kind: 'reversal', label: 'ຄືນໜີ້', up: true},
];

// `YYYY-MM-DD` of a local date — the operator's calendar day, not UTC's.
function isoDay(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}

function addDays(day, n) {
  const [y, m, d] = day.split('-').map(Number);
  return isoDay(new Date(y, m - 1, d + n));
}

export default {
  name: 'DebtHistoryTab',
  components: {Pagination},
  data() {
    const today = isoDay(new Date());
    return {
      rows: [],
      days: [],
      totals: {},
      advances: [],
      filters: {from: today, to: today, allDates: false, kind: 'deductions', search: ''},
      kinds: [
        {value: 'deductions', label: 'ການຫັກໜີ້ທັງໝົດ'},
        {value: 'auto_recovery', label: 'ຫັກໜີ້ອັດຕະໂນມັດ'},
        {value: 'repayment', label: 'ຮັບຊຳລະໜີ້'},
        // No ຫັກຕອນຖອນເງິນ: since 2026-10-01 nothing can be requested while a
        // debt is owed, so a withdrawal can no longer repay one.
        {value: 'write_off', label: 'ຕັດໜີ້'},
        {value: 'all', label: 'ທຸກການເຄື່ອນໄຫວ (ລວມໂອນລ່ວງໜ້າ)'},
      ],
      pagination: {current_page: 1, last_page: 1, per_page: 20, total: 0},
      error: null,
      isLoading: false,
      requestSeq: 0,
      slipActive: false,
      slip: {url: '', shopName: '', amount: 0, paidAt: null, failed: false},
      exporting: false,
      invoiceActive: false,
      invoiceLoading: false,
      invoiceError: null,
      invoiceId: null,
      invoiceSeq: 0,
      invoiceView: {},
    };
  },
  computed: {
    inv() {
      return this.invoiceView.invoice || null;
    },
    // A copy for Pagination, which writes current_page into its prop.
    invoicePagination() {
      const v = this.invoiceView;
      return {current_page: v.current_page, last_page: v.last_page, per_page: v.per_page, total: v.total};
    },
  },
  created() {
    this.fetchData(1);
  },
  methods: {
    formatDateTime,
    formatKip,
    openSlip(row) {
      this.slip = {
        url: row.slip_url,
        shopName: row.shop.name || row.customer.name || '—',
        amount: Math.abs(row.amount),
        paidAt: row.paid_at,
        failed: false,
      };
      this.slipActive = true;
    },
    // The row's invoice in a few short lines; each figure under its own name.
    invoiceLines(inv) {
      const lines = [];
      if (inv.qty) lines.push('ຍອດ COD ' + formatKip(inv.cod_total) + ' · ' + inv.qty + ' ບິນ');
      if (inv.advance_amount) lines.push('ເງິນລ່ວງໜ້າ ' + formatKip(inv.advance_amount));
      if (inv.payout) {
        lines.push('ໂອນໃຫ້ຮ້ານ ' + formatKip(inv.payout) + ' · ' + inv.status_label);
      } else if (inv.status === 'rejected') {
        lines.push(inv.status_label);
      }
      return lines;
    },
    openInvoice(id) {
      if (!id) return;
      this.invoiceId = id;
      this.invoiceView = {};
      this.invoiceActive = true;
      this.loadInvoicePage(1);
    },
    async loadInvoicePage(page) {
      const seq = ++this.invoiceSeq;
      this.invoiceError = null;
      this.invoiceLoading = true;
      try {
        const data = await fetchDebtHistoryInvoice(this.invoiceId, page || 1);
        if (seq !== this.invoiceSeq) return;
        this.invoiceView = data || {};
      } catch (e) {
        if (seq !== this.invoiceSeq) return;
        // Pagination already wrote the requested page into its prop: a fresh
        // object puts it back on the page whose bills are still shown.
        this.invoiceView = {...this.invoiceView};
        this.invoiceError = errorMessage(e, 'ໂຫລດອິນວອຍບໍ່ສຳເລັດ');
      } finally {
        if (seq === this.invoiceSeq) this.invoiceLoading = false;
      }
    },
    // Every movement of an advance, whatever its date: the table filtered to its number.
    traceInvoice(number) {
      this.invoiceActive = false;
      this.filters.search = number;
      this.filters.allDates = true;
      this.filters.kind = 'all';
      this.fetchData(1);
    },
    params(page) {
      const range = this.filters.allDates
        ? {all_dates: 1}
        : {from: this.filters.from, to: this.filters.to};
      return {
        ...range,
        kind: this.filters.kind,
        search: this.filters.search || undefined,
        page: page || this.pagination.current_page,
      };
    },
    async exportRows() {
      if (this.exporting) return;
      this.exporting = true;
      this.error = null;
      try {
        // Named from the filters sent, not the live ones: they may change mid-download.
        const filters = this.params(1);
        delete filters.page;
        const range = filters.all_dates ? 'all' : filters.from + '_' + filters.to;
        const response = await downloadDebtHistoryExport(filters);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'COD Advance Debt History ' + range + '.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.error = await blobErrorMessage(e, 'Export ບໍ່ສຳເລັດ');
      } finally {
        this.exporting = false;
      }
    },
    dayLines(d) {
      const invoices = d.invoices || {};
      return DAY_KINDS.filter((k) => d[k.kind]).map((k) => ({
        ...k,
        amount: d[k.kind],
        invoices: invoices[k.kind] && invoices[k.kind].numbers.length ? invoices[k.kind] : null,
      }));
    },
    change(row) {
      const down = row.amount < 0;
      return {
        tone: down ? 'text-ok' : 'text-bad',
        text: (down ? '−' : '+') + formatKip(Math.abs(row.amount)),
      };
    },
    // Moves the whole range, so a single day steps to the day before / after.
    shiftDays(n) {
      this.filters.from = addDays(this.filters.from, n);
      this.filters.to = addDays(this.filters.to, n);
      this.fetchData(1);
    },
    pickDay(day) {
      this.filters.from = day;
      this.filters.to = day;
      this.fetchData(1);
    },
    onDates() {
      if (!this.filters.from) this.filters.from = this.filters.to;
      if (!this.filters.to) this.filters.to = this.filters.from;
      if (this.filters.to < this.filters.from) {
        this.filters.to = this.filters.from;
      }
      if (addDays(this.filters.from, MAX_DAYS - 1) < this.filters.to) {
        this.filters.to = addDays(this.filters.from, MAX_DAYS - 1);
      }
      this.fetchData(1);
    },
    async fetchData(page) {
      const seq = ++this.requestSeq;
      this.error = null;
      this.isLoading = true;
      try {
        const data = await fetchDebtHistory(this.params(page));
        // A slower answer to an older filter must not overwrite a newer one.
        if (seq !== this.requestSeq) return;
        this.rows = data.data || [];
        this.days = data.days || [];
        this.totals = data.totals || {};
        this.advances = data.advances || [];
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        };
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.rows = [];
        this.days = [];
        this.totals = {};
        this.advances = [];
        this.error = errorMessage(e, 'ໂຫລດຂໍ້ມູນບໍ່ສຳເລັດ');
      } finally {
        if (seq === this.requestSeq) this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.day-step {
  display: flex;
  align-items: center;
  gap: 6px;
}

.round-btn.is-step {
  width: 30px;
  height: 30px;
  font-size: 13px;
}

.filter-input.is-date {
  width: 150px;
}

.filter-input.is-wide {
  width: 220px;
}

.filter-input:disabled,
.round-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.all-dates {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
  height: 36px;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
}

.advance-card {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 18px;
  padding: 12px 16px;
  margin-bottom: 14px;
  border: 1px solid $cod-blue-pale;
  border-radius: 10px;
  background: #f7f9ff;
  font-size: 13px;
  color: #4a5162;

  .advance-title {
    font-weight: 600;
  }
}

.day-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 14px;
}

.day-chip {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 170px;
  padding: 10px 12px;
  border: 1px solid $cod-line;
  border-radius: 10px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  color: $cod-ink;

  &:hover {
    border-color: $cod-blue-pale;
  }

  &.is-active {
    border-color: $cod-blue;
    box-shadow: 0 0 0 3px rgba(59, 91, 254, 0.12);
  }
}

.day-date {
  font-size: 13px;
  margin-bottom: 2px;
}

.day-line {
  font-size: 12px;
  color: #4a5162;

  &.is-up {
    color: $cod-muted;
  }
}

.day-invoices {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
  margin-bottom: 2px;
  font-size: 11px;
  line-height: 1.5;
  color: $cod-muted;
  font-variant-numeric: tabular-nums;
}

.day-more {
  font-style: italic;
}

.day-sum {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #1e8e4e;
}

.kind-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  background: #f2f4f7;
  color: #4a5162;

  &.is-auto_recovery {
    background: #e6f4ec;
    color: #1e8e4e;
  }

  &.is-withdraw_recovery {
    background: #dbe3fd;
    color: $cod-blue;
  }

  &.is-write_off {
    background: #fdecee;
    color: $cod-danger;
  }

  &.is-repayment {
    background: #e8f1fd;
    color: $cod-blue;
  }
}

.link-btn {
  border: 0;
  background: none;
  padding: 0;
  color: $cod-blue;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &.is-note {
    font-size: inherit;
  }
}

.cell-note.is-figure {
  color: #4a5162;
}

.invoice-view {
  .info-box {
    margin: 0 0 14px;
  }

  .trace-btn {
    margin-top: 8px;
  }
}

.info-note {
  margin: 6px 0 0;
  font-size: 12px;
  color: $cod-muted;
}

.bill-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 10px;

  th,
  td {
    padding: 7px 8px;
    border-bottom: 1px solid $cod-line;
    text-align: left;
  }

  th {
    color: $cod-muted;
    font-weight: 600;
  }

  .is-num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

.dialog-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  width: 100%;
}

.slip-view .info-box {
  margin: 0 0 14px;
}

.slip-image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 320px);
  min-height: 200px;
  margin: 0 auto;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid $cod-line;
}
</style>
